// Software-cluster landing pages — trilingual copy (en / fr / es).
// Per the integration policy: every approach item is tagged Native / Configuration /
// Integration / Custom API so buyers can tell platform capability from custom work.
// Zuper page states plainly that Ocean Data is not a certified Zuper partner.

export const CLUSTER_COPY = {
  "zoho-fsm": {
    en: {
      path: "/zoho-fsm",
      docTitle: "Zoho FSM Implementation for HVAC, Plumbing & Field Service | Ocean Data",
      schemaName: "Zoho FSM implementation for field service businesses",
      eyebrow: "Software guide · Zoho FSM",
      h1: "Zoho FSM, implemented around how your field team actually works.",
      sub: "Scheduling, dispatch, work orders, service completion and invoicing — set up for HVAC, plumbing and field service businesses, and connected to Zoho Books or QuickBooks so the office stops re-typing what the field already knows.",
      painsTitle: "Where field service hours leak",
      pains: [
        "Technicians call the office for job details that are already in the system",
        "Work orders get completed in the field and re-entered at night",
        "Invoices wait days for paperwork to come back",
        "The schedule lives on a whiteboard nobody can see from the road",
      ],
      approachTitle: "How an Ocean Data FSM implementation works",
      approach: [
        { tag: "Native", text: "Scheduling, dispatch, mobile work orders and service reports — configured inside Zoho FSM itself, no custom code." },
        { tag: "Configuration", text: "Services, price books, territories and skills mapped to the way your team actually quotes and dispatches." },
        { tag: "Integration", text: "Accounting handoff to Zoho Books natively, or to QuickBooks through the supported connector — invoices without re-entry." },
        { tag: "Custom API", text: "Where a workflow isn't covered — a niche price book, a legacy tool — we build it explicitly and document the limits." },
      ],
      honestNote: "Honest note: Zoho FSM covers most SMB field service workflows well. Where it doesn't — deep multi-branch inventory, for example — we say so in the review and point you at the right tool, even if it isn't ours to implement.",
      faqTitle: "The honest answers.",
      faqs: [
        {
          q: "Does Zoho FSM work with QuickBooks?",
          a: "Yes — through the supported QuickBooks integration, so you can keep QuickBooks accounting while FSM runs field operations. Where a specific sync isn't covered, we flag it before you commit.",
        },
        {
          q: "How long does an FSM implementation take?",
          a: "A single-process implementation — dispatch to invoice — is typically scoped inside a Project engagement after the free process mapping audit. Wider rollouts are scoped together.",
        },
      ],
      cta: "Book my free 15-minute review",
    },
    fr: {
      path: "/fr/zoho-fsm",
      docTitle: "Implantation Zoho FSM pour CVAC, plomberie et services sur le terrain | Ocean Data",
      schemaName: "Implantation Zoho FSM pour entreprises de services sur le terrain",
      eyebrow: "Guide logiciel · Zoho FSM",
      h1: "Zoho FSM, implanté selon la façon dont votre équipe de terrain travaille vraiment.",
      sub: "Planification, répartition, bons de travail, constats de service et facturation — configurés pour les entreprises de CVAC, de plomberie et de services sur le terrain, et connectés à Zoho Books ou QuickBooks pour que le bureau cesse de ressaisir ce que le terrain sait déjà.",
      painsTitle: "Où les heures de service fuient",
      pains: [
        "Les techniciens appellent le bureau pour des détails déjà dans le système",
        "Les bons de travail se complètent sur le terrain et se ressaisissent le soir",
        "Les factures attendent des jours le retour de la paperasse",
        "L'horaire vit sur un tableau blanc que personne ne voit de la route",
      ],
      approachTitle: "Comment fonctionne une implantation FSM par Ocean Data",
      approach: [
        { tag: "Natif", text: "Planification, répartition, bons de travail mobiles et rapports de service — configurés dans Zoho FSM même, sans code sur mesure." },
        { tag: "Configuration", text: "Services, listes de prix, territoires et compétences alignés sur la façon dont votre équipe soumissionne et répartit vraiment." },
        { tag: "Intégration", text: "Transfert comptable vers Zoho Books en natif, ou vers QuickBooks par le connecteur supporté — des factures sans ressaisie." },
        { tag: "API sur mesure", text: "Quand un flux n'est pas couvert — une liste de prix niche, un outil existant — on le construit explicitement et on en documente les limites." },
      ],
      honestNote: "Note honnête : Zoho FSM couvre bien la plupart des flux de service des PME. Là où ce n'est pas le cas — l'inventaire multi-succursales poussé, par exemple — on le dit dans la revue et on vous oriente vers le bon outil, même si ce n'est pas nous qui l'implantons.",
      faqTitle: "Les réponses honnêtes.",
      faqs: [
        {
          q: "Est-ce que Zoho FSM fonctionne avec QuickBooks ?",
          a: "Oui — par l'intégration QuickBooks supportée, pour garder votre comptabilité QuickBooks pendant que FSM gère les opérations de terrain. Si une synchronisation précise n'est pas couverte, on le signale avant que vous vous engagiez.",
        },
        {
          q: "Combien de temps prend une implantation FSM ?",
          a: "Une implantation à processus unique — de la répartition à la facture — se cadre typiquement dans un mandat Projet après l'audit de cartographie gratuit. Les déploiements plus larges se conçoivent ensemble.",
        },
      ],
      cta: "Réserver ma revue gratuite de 15 minutes",
    },
    es: {
      path: "/es/zoho-fsm",
      docTitle: "Implementación de Zoho FSM para HVAC, plomería y servicio de campo | Ocean Data",
      schemaName: "Implementación de Zoho FSM para empresas de servicio de campo",
      eyebrow: "Guía de software · Zoho FSM",
      h1: "Zoho FSM, implementado según cómo su equipo de campo realmente trabaja.",
      sub: "Programación, despacho, órdenes de trabajo, constancias de servicio y facturación — configurados para empresas de HVAC, plomería y servicio de campo, y conectados a Zoho Books o QuickBooks para que la oficina deje de reescribir lo que el campo ya sabe.",
      painsTitle: "Dónde se escapan las horas de servicio",
      pains: [
        "Los técnicos llaman a la oficina por detalles que ya están en el sistema",
        "Las órdenes de trabajo se completan en el campo y se reingresan de noche",
        "Las facturas esperan días a que regrese la papelería",
        "El horario vive en una pizarra que nadie ve desde la carretera",
      ],
      approachTitle: "Cómo funciona una implementación de FSM con Ocean Data",
      approach: [
        { tag: "Nativo", text: "Programación, despacho, órdenes de trabajo móviles e informes de servicio — configurados dentro del propio Zoho FSM, sin código a medida." },
        { tag: "Configuración", text: "Servicios, listas de precios, territorios y habilidades alineados a cómo su equipo realmente cotiza y despacha." },
        { tag: "Integración", text: "Traspaso contable a Zoho Books de forma nativa, o a QuickBooks mediante el conector soportado — facturas sin reescritura." },
        { tag: "API a medida", text: "Cuando un flujo no está cubierto — una lista de precios de nicho, una herramienta heredada — lo construimos explícitamente y documentamos los límites." },
      ],
      honestNote: "Nota honesta: Zoho FSM cubre bien la mayoría de los flujos de servicio de las pymes. Donde no — inventario profundo multisucursal, por ejemplo — lo decimos en la revisión y le orientamos hacia la herramienta correcta, aunque no seamos nosotros quienes la implementemos.",
      faqTitle: "Las respuestas honestas.",
      faqs: [
        {
          q: "¿Zoho FSM funciona con QuickBooks?",
          a: "Sí — mediante la integración soportada de QuickBooks, para conservar su contabilidad en QuickBooks mientras FSM opera el campo. Si una sincronización concreta no está cubierta, lo señalamos antes de que se comprometa.",
        },
        {
          q: "¿Cuánto tarda una implementación de FSM?",
          a: "Una implementación de un solo proceso — del despacho a la factura — se enmarca típicamente en un Proyecto tras la auditoría gratuita de mapeo de procesos. Los despliegues más amplios se definen juntos.",
        },
      ],
      cta: "Reservar mi revisión gratuita de 15 minutos",
    },
  },

  "zoho-books-vs-quickbooks": {
    en: {
      path: "/zoho-books-vs-quickbooks",
      docTitle: "Zoho Books or QuickBooks for Contractors — Implementation & Migration | Ocean Data",
      schemaName: "Zoho Books implementation and QuickBooks integration for contractors",
      eyebrow: "Software guide · Zoho Books & QuickBooks",
      h1: "Zoho Books or QuickBooks — your accounting, connected to the work.",
      sub: "We implement Zoho Books for contractors, HVAC and plumbing businesses — or connect the QuickBooks you already keep — so invoices, payments and job costs flow from the field without re-entry.",
      painsTitle: "Where the books lose hours",
      pains: [
        "Invoices are built from memory days after the job",
        "Job costing lives in a spreadsheet only one person understands",
        "Payments get chased by phone because nobody sees what's outstanding",
        "The books and the field tell two different stories",
      ],
      approachTitle: "How we connect your accounting",
      approach: [
        { tag: "Native", text: "Zoho Books invoicing, estimates, payments and expense tracking configured for trade businesses — progress billing included." },
        { tag: "Configuration", text: "Chart of accounts, tax rules and item lists mapped to how you actually bill — time-and-materials, flat rate, or progress." },
        { tag: "Integration", text: "QuickBooks stays if it works for you — we connect it to your field tools, including Zoho FSM, through supported connectors." },
        { tag: "Custom API", text: "Migrating from QuickBooks to Zoho Books? We move history deliberately, reconcile, and document what changes — no silent cutovers." },
      ],
      honestNote: "Honest note: we recommend Zoho Books when it genuinely fits — and say so when QuickBooks (or your accountant's preference) makes staying put the right call. The goal is connected books, not a new logo.",
      faqTitle: "The honest answers.",
      faqs: [
        {
          q: "Should we switch from QuickBooks to Zoho Books?",
          a: "Only if the switch pays for itself in hours returned. If QuickBooks serves you well, we connect it instead. The free review gives you a straight answer either way.",
        },
        {
          q: "Can Zoho Books handle progress billing?",
          a: "Yes — retainage-style workflows and progress invoicing can be configured for construction billing, connected to Zoho Projects milestones.",
        },
      ],
      cta: "Book my free 15-minute review",
    },
    fr: {
      path: "/fr/zoho-books-vs-quickbooks",
      docTitle: "Zoho Books ou QuickBooks pour entrepreneurs — implantation et migration | Ocean Data",
      schemaName: "Implantation Zoho Books et intégration QuickBooks pour entrepreneurs",
      eyebrow: "Guide logiciel · Zoho Books et QuickBooks",
      h1: "Zoho Books ou QuickBooks — votre comptabilité, connectée au travail.",
      sub: "On implante Zoho Books pour les entrepreneurs, les entreprises de CVAC et de plomberie — ou on connecte le QuickBooks que vous gardez déjà — pour que factures, paiements et coûts de chantier coulent du terrain sans ressaisie.",
      painsTitle: "Où la comptabilité perd des heures",
      pains: [
        "Les factures se construisent de mémoire des jours après le travail",
        "Le coût des chantiers vit dans un tableur qu'une seule personne comprend",
        "Les paiements se courent après au téléphone faute de visibilité",
        "Les livres et le terrain racontent deux histoires différentes",
      ],
      approachTitle: "Comment on connecte votre comptabilité",
      approach: [
        { tag: "Natif", text: "Facturation, soumissions, paiements et dépenses Zoho Books configurés pour les métiers — facturation progressive incluse." },
        { tag: "Configuration", text: "Plan comptable, règles de taxes et listes d'items alignés sur votre façon de facturer — temps et matériaux, forfait ou progrès." },
        { tag: "Intégration", text: "QuickBooks reste s'il vous sert bien — on le connecte à vos outils de terrain, dont Zoho FSM, par les connecteurs supportés." },
        { tag: "API sur mesure", text: "Migration de QuickBooks vers Zoho Books ? On déplace l'historique délibérément, on concilie, et on documente ce qui change — aucune bascule silencieuse." },
      ],
      honestNote: "Note honnête : on recommande Zoho Books quand il convient vraiment — et on le dit quand QuickBooks (ou la préférence de votre comptable) fait de rester le bon choix. L'objectif, c'est une comptabilité connectée, pas un nouveau logo.",
      faqTitle: "Les réponses honnêtes.",
      faqs: [
        {
          q: "Devrions-nous passer de QuickBooks à Zoho Books ?",
          a: "Seulement si le changement se rembourse en heures rendues. Si QuickBooks vous sert bien, on le connecte plutôt. La revue gratuite vous donne une réponse franche dans les deux cas.",
        },
        {
          q: "Est-ce que Zoho Books gère la facturation progressive ?",
          a: "Oui — les flux de type retenue et la facturation d'avancement se configurent pour la facturation de construction, connectés aux jalons de Zoho Projects.",
        },
      ],
      cta: "Réserver ma revue gratuite de 15 minutes",
    },
    es: {
      path: "/es/zoho-books-vs-quickbooks",
      docTitle: "Zoho Books o QuickBooks para contratistas — implementación y migración | Ocean Data",
      schemaName: "Implementación de Zoho Books e integración con QuickBooks para contratistas",
      eyebrow: "Guía de software · Zoho Books y QuickBooks",
      h1: "Zoho Books o QuickBooks — su contabilidad, conectada al trabajo.",
      sub: "Implementamos Zoho Books para contratistas y empresas de HVAC y plomería — o conectamos el QuickBooks que ya conserva — para que facturas, pagos y costos de obra fluyan desde el campo sin reescritura.",
      painsTitle: "Dónde la contabilidad pierde horas",
      pains: [
        "Las facturas se arman de memoria días después del trabajo",
        "El costo de obra vive en una hoja de cálculo que una sola persona entiende",
        "Los pagos se persiguen por teléfono porque nadie ve lo pendiente",
        "Los libros y el campo cuentan dos historias distintas",
      ],
      approachTitle: "Cómo conectamos su contabilidad",
      approach: [
        { tag: "Nativo", text: "Facturación, cotizaciones, pagos y gastos de Zoho Books configurados para oficios — facturación por avance incluida." },
        { tag: "Configuración", text: "Plan de cuentas, reglas de impuestos y listas de artículos alineados a cómo factura realmente — tiempo y materiales, precio fijo o avance." },
        { tag: "Integración", text: "QuickBooks se queda si le funciona — lo conectamos a sus herramientas de campo, incluido Zoho FSM, mediante conectores soportados." },
        { tag: "API a medida", text: "¿Migrar de QuickBooks a Zoho Books? Movemos el historial deliberadamente, conciliamos y documentamos lo que cambia — sin cambios silenciosos." },
      ],
      honestNote: "Nota honesta: recomendamos Zoho Books cuando realmente encaja — y lo decimos cuando QuickBooks (o la preferencia de su contador) hace que quedarse sea la decisión correcta. La meta es una contabilidad conectada, no un logo nuevo.",
      faqTitle: "Las respuestas honestas.",
      faqs: [
        {
          q: "¿Deberíamos cambiar de QuickBooks a Zoho Books?",
          a: "Solo si el cambio se paga solo en horas devueltas. Si QuickBooks le sirve bien, lo conectamos en su lugar. La revisión gratuita le da una respuesta franca en cualquier caso.",
        },
        {
          q: "¿Zoho Books maneja facturación por avance?",
          a: "Sí — los flujos tipo retención y la facturación por progreso se configuran para la facturación de construcción, conectados a los hitos de Zoho Projects.",
        },
      ],
      cta: "Reservar mi revisión gratuita de 15 minutos",
    },
  },

  "zoho-iot-connected-buildings": {
    en: {
      path: "/zoho-iot-connected-buildings",
      docTitle: "Zoho IoT Connected Buildings — HVAC Asset & Energy Monitoring | Ocean Data",
      schemaName: "Zoho IoT connected building and HVAC asset monitoring workflows",
      eyebrow: "Software guide · Zoho IoT",
      h1: "Connected buildings: equipment telemetry your team can act on.",
      sub: "Zoho IoT for facilities and HVAC asset monitoring — connected equipment, energy visibility and maintenance workflows, tied into your CRM or field service system where the implementation supports it.",
      painsTitle: "What unmonitored buildings cost you",
      pains: [
        "Equipment failures are discovered by tenants, not sensors",
        "Energy waste is visible only on the monthly utility bill",
        "Maintenance is calendar-based instead of condition-based",
        "Building data lives in dashboards nobody opens",
      ],
      approachTitle: "How we connect your buildings",
      approach: [
        { tag: "Native", text: "Zoho IoT device onboarding, dashboards and alerts for connected assets — HVAC units, energy meters, critical equipment." },
        { tag: "Configuration", text: "Alert thresholds and escalation rules tuned so the right person hears about the right anomaly — not every blip." },
        { tag: "Integration", text: "Telemetry linked to your CRM or Zoho FSM so an alert can become a work order automatically, with the asset's history attached." },
        { tag: "Custom API", text: "Unsupported hardware or a BMS protocol? We assess feasibility honestly and build connectors only where they're maintainable." },
      ],
      honestNote: "Honest note: IoT projects live or die on hardware compatibility. We validate your actual equipment list before proposing anything — where support isn't there, we tell you before you spend, not after.",
      faqTitle: "The honest answers.",
      faqs: [
        {
          q: "What equipment can Zoho IoT monitor?",
          a: "Anything with a supported sensor or gateway path — HVAC units, compressors, energy meters, temperature-sensitive assets. Bring your equipment list to the review; we'll tell you what's coverable.",
        },
        {
          q: "Can alerts become work orders automatically?",
          a: "Yes — connected to Zoho FSM or your CRM, a threshold breach can open a work order with the asset's service history attached.",
        },
      ],
      cta: "Book my free 15-minute review",
    },
    fr: {
      path: "/fr/zoho-iot-connected-buildings",
      docTitle: "Bâtiments connectés Zoho IoT — surveillance des actifs CVAC et de l'énergie | Ocean Data",
      schemaName: "Flux de bâtiments connectés Zoho IoT et de surveillance d'actifs CVAC",
      eyebrow: "Guide logiciel · Zoho IoT",
      h1: "Bâtiments connectés : une télémétrie d'équipement sur laquelle votre équipe peut agir.",
      sub: "Zoho IoT pour la surveillance des installations et des actifs CVAC — équipement connecté, visibilité énergétique et flux de maintenance, reliés à votre CRM ou à votre système de service sur le terrain là où l'implantation le supporte.",
      painsTitle: "Ce que coûtent les bâtiments non surveillés",
      pains: [
        "Les pannes d'équipement sont découvertes par les locataires, pas par des capteurs",
        "Le gaspillage d'énergie n'est visible que sur la facture mensuelle",
        "La maintenance suit un calendrier plutôt que l'état réel",
        "Les données du bâtiment vivent dans des tableaux de bord que personne n'ouvre",
      ],
      approachTitle: "Comment on connecte vos bâtiments",
      approach: [
        { tag: "Natif", text: "Ajout d'appareils, tableaux de bord et alertes Zoho IoT pour les actifs connectés — unités CVAC, compteurs d'énergie, équipement critique." },
        { tag: "Configuration", text: "Seuils d'alerte et règles d'escalade réglés pour que la bonne personne entende la bonne anomalie — pas chaque pépin." },
        { tag: "Intégration", text: "Télémétrie reliée à votre CRM ou à Zoho FSM pour qu'une alerte devienne un bon de travail automatiquement, avec l'historique de l'actif." },
        { tag: "API sur mesure", text: "Matériel non supporté ou protocole GTB ? On évalue la faisabilité honnêtement et on ne construit des connecteurs que là où ils restent maintenables." },
      ],
      honestNote: "Note honnête : les projets IoT tiennent ou tombent sur la compatibilité du matériel. On valide votre liste d'équipement réelle avant de proposer quoi que ce soit — là où le support n'existe pas, on vous le dit avant que vous dépensiez, pas après.",
      faqTitle: "Les réponses honnêtes.",
      faqs: [
        {
          q: "Quel équipement Zoho IoT peut-il surveiller ?",
          a: "Tout ce qui a un capteur ou une passerelle supportés — unités CVAC, compresseurs, compteurs d'énergie, actifs sensibles à la température. Apportez votre liste d'équipement à la revue; on vous dira ce qui est couvrable.",
        },
        {
          q: "Les alertes peuvent-elles devenir des bons de travail automatiquement ?",
          a: "Oui — connectées à Zoho FSM ou à votre CRM, un dépassement de seuil peut ouvrir un bon de travail avec l'historique de service de l'actif.",
        },
      ],
      cta: "Réserver ma revue gratuite de 15 minutes",
    },
    es: {
      path: "/es/zoho-iot-connected-buildings",
      docTitle: "Edificios conectados Zoho IoT — monitoreo de activos HVAC y energía | Ocean Data",
      schemaName: "Flujos de edificios conectados Zoho IoT y monitoreo de activos HVAC",
      eyebrow: "Guía de software · Zoho IoT",
      h1: "Edificios conectados: telemetría de equipos sobre la que su equipo puede actuar.",
      sub: "Zoho IoT para el monitoreo de instalaciones y activos HVAC — equipos conectados, visibilidad energética y flujos de mantenimiento, vinculados a su CRM o sistema de servicio de campo donde la implementación lo soporte.",
      painsTitle: "Lo que cuestan los edificios sin monitoreo",
      pains: [
        "Las fallas de equipo las descubren los inquilinos, no los sensores",
        "El desperdicio de energía solo se ve en la factura mensual",
        "El mantenimiento sigue un calendario en vez del estado real",
        "Los datos del edificio viven en tableros que nadie abre",
      ],
      approachTitle: "Cómo conectamos sus edificios",
      approach: [
        { tag: "Nativo", text: "Alta de dispositivos, tableros y alertas de Zoho IoT para activos conectados — unidades HVAC, medidores de energía, equipo crítico." },
        { tag: "Configuración", text: "Umbrales de alerta y reglas de escalado ajustados para que la persona correcta se entere de la anomalía correcta — no de cada parpadeo." },
        { tag: "Integración", text: "Telemetría vinculada a su CRM o Zoho FSM para que una alerta se convierta en orden de trabajo automáticamente, con el historial del activo." },
        { tag: "API a medida", text: "¿Hardware no soportado o protocolo BMS? Evaluamos la viabilidad con honestidad y solo construimos conectores donde sean mantenibles." },
      ],
      honestNote: "Nota honesta: los proyectos de IoT viven o mueren por la compatibilidad del hardware. Validamos su lista real de equipos antes de proponer nada — donde no hay soporte, se lo decimos antes de que gaste, no después.",
      faqTitle: "Las respuestas honestas.",
      faqs: [
        {
          q: "¿Qué equipos puede monitorear Zoho IoT?",
          a: "Cualquiera con un sensor o pasarela soportados — unidades HVAC, compresores, medidores de energía, activos sensibles a la temperatura. Traiga su lista de equipos a la revisión; le diremos qué es cubrible.",
        },
        {
          q: "¿Las alertas pueden volverse órdenes de trabajo automáticamente?",
          a: "Sí — conectadas a Zoho FSM o a su CRM, un umbral superado puede abrir una orden de trabajo con el historial de servicio del activo.",
        },
      ],
      cta: "Reservar mi revisión gratuita de 15 minutos",
    },
  },

  "zuper-field-service": {
    en: {
      path: "/zuper-field-service",
      docTitle: "Zuper Field Service — Evaluation & Implementation Consulting | Ocean Data",
      schemaName: "Zuper field service evaluation and implementation consulting",
      eyebrow: "Software guide · Zuper",
      h1: "Considering Zuper? Get an honest evaluation before you commit.",
      sub: "We help HVAC, plumbing and field service businesses evaluate, implement or integrate Zuper — with the same discipline we bring to any platform: your workflow first, the software second.",
      painsTitle: "Why field service rollouts stall",
      pains: [
        "You're comparing platforms on feature lists, not on your workflow",
        "A previous rollout failed because the tool was chosen before the process was mapped",
        "Data lives in the FSM tool but accounting still re-keys everything",
        "Nobody can tell you what the switch will actually cost in disruption",
      ],
      approachTitle: "How we work with Zuper",
      approach: [
        { tag: "Evaluation", text: "A structured fit assessment: your dispatch model, quoting flow and accounting stack against Zuper's real capabilities — including where it doesn't fit." },
        { tag: "Implementation", text: "If Zuper fits, we configure scheduling, work orders and mobile workflows around your team's actual day." },
        { tag: "Integration", text: "Accounting handoffs to QuickBooks or Zoho Books through supported connectors, with the sync limits documented up front." },
        { tag: "Advisory", text: "If Zoho FSM or another platform serves you better, we say so. The recommendation follows your workflow, not our preference." },
      ],
      honestNote: "Transparency: Ocean Data is not a certified Zuper partner or reseller. We advise on evaluation, implementation and integration based on field service experience — and we'll tell you when a certified partner is the better route.",
      faqTitle: "The honest answers.",
      faqs: [
        {
          q: "Zuper or Zoho FSM — which is better?",
          a: "It depends on your dispatch model, team size and accounting stack. Zoho FSM fits Zoho-centric businesses; Zuper can suit teams needing its specific scheduling strengths. The free review maps your workflow against both.",
        },
        {
          q: "Can you integrate Zuper with QuickBooks?",
          a: "Where a supported connector exists, yes — and we document exactly what syncs and what doesn't before you decide.",
        },
      ],
      cta: "Book my free 15-minute review",
    },
    fr: {
      path: "/fr/zuper-field-service",
      docTitle: "Zuper pour services sur le terrain — évaluation et implantation | Ocean Data",
      schemaName: "Conseil en évaluation et implantation Zuper pour services sur le terrain",
      eyebrow: "Guide logiciel · Zuper",
      h1: "Vous envisagez Zuper ? Obtenez une évaluation honnête avant de vous engager.",
      sub: "On aide les entreprises de CVAC, de plomberie et de services sur le terrain à évaluer, implanter ou intégrer Zuper — avec la même discipline que pour toute plateforme : votre flux de travail d'abord, le logiciel ensuite.",
      painsTitle: "Pourquoi les déploiements de service calent",
      pains: [
        "Vous comparez les plateformes sur des listes de fonctions, pas sur votre flux de travail",
        "Un déploiement précédent a échoué parce que l'outil a été choisi avant la cartographie du processus",
        "Les données vivent dans l'outil FSM mais la comptabilité ressaisit tout",
        "Personne ne peut vous dire ce que le changement coûtera vraiment en perturbation",
      ],
      approachTitle: "Comment on travaille avec Zuper",
      approach: [
        { tag: "Évaluation", text: "Une évaluation d'adéquation structurée : votre modèle de répartition, votre flux de soumission et votre pile comptable face aux capacités réelles de Zuper — y compris là où il ne convient pas." },
        { tag: "Implantation", text: "Si Zuper convient, on configure planification, bons de travail et flux mobiles autour de la vraie journée de votre équipe." },
        { tag: "Intégration", text: "Transferts comptables vers QuickBooks ou Zoho Books par les connecteurs supportés, avec les limites de synchronisation documentées d'avance." },
        { tag: "Conseil", text: "Si Zoho FSM ou une autre plateforme vous sert mieux, on le dit. La recommandation suit votre flux de travail, pas notre préférence." },
      ],
      honestNote: "Transparence : Ocean Data n'est pas un partenaire certifié ni un revendeur Zuper. On conseille sur l'évaluation, l'implantation et l'intégration à partir de l'expérience des services sur le terrain — et on vous dira quand un partenaire certifié est la meilleure voie.",
      faqTitle: "Les réponses honnêtes.",
      faqs: [
        {
          q: "Zuper ou Zoho FSM — lequel est meilleur ?",
          a: "Cela dépend de votre modèle de répartition, de la taille de l'équipe et de votre pile comptable. Zoho FSM convient aux entreprises centrées Zoho; Zoho FSM ou Zuper peuvent chacun gagner selon vos forces de planification. La revue gratuite cartographie votre flux face aux deux.",
        },
        {
          q: "Pouvez-vous intégrer Zuper à QuickBooks ?",
          a: "Là où un connecteur supporté existe, oui — et on documente exactement ce qui se synchronise et ce qui ne se synchronise pas avant que vous décidiez.",
        },
      ],
      cta: "Réserver ma revue gratuite de 15 minutes",
    },
    es: {
      path: "/es/zuper-field-service",
      docTitle: "Zuper para servicio de campo — evaluación e implementación | Ocean Data",
      schemaName: "Consultoría de evaluación e implementación de Zuper para servicio de campo",
      eyebrow: "Guía de software · Zuper",
      h1: "¿Considera Zuper? Obtenga una evaluación honesta antes de comprometerse.",
      sub: "Ayudamos a empresas de HVAC, plomería y servicio de campo a evaluar, implementar o integrar Zuper — con la misma disciplina que aplicamos a cualquier plataforma: su flujo de trabajo primero, el software después.",
      painsTitle: "Por qué se estancan los despliegues de servicio",
      pains: [
        "Compara plataformas por listas de funciones, no por su flujo de trabajo",
        "Un despliegue anterior falló porque la herramienta se eligió antes de mapear el proceso",
        "Los datos viven en la herramienta FSM pero contabilidad lo reescribe todo",
        "Nadie puede decirle lo que el cambio costará realmente en disrupción",
      ],
      approachTitle: "Cómo trabajamos con Zuper",
      approach: [
        { tag: "Evaluación", text: "Una evaluación de encaje estructurada: su modelo de despacho, su flujo de cotización y su pila contable frente a las capacidades reales de Zuper — incluyendo dónde no encaja." },
        { tag: "Implementación", text: "Si Zuper encaja, configuramos programación, órdenes de trabajo y flujos móviles alrededor del día real de su equipo." },
        { tag: "Integración", text: "Traspasos contables a QuickBooks o Zoho Books mediante conectores soportados, con los límites de sincronización documentados desde el inicio." },
        { tag: "Asesoría", text: "Si Zoho FSM u otra plataforma le sirve mejor, lo decimos. La recomendación sigue a su flujo de trabajo, no a nuestra preferencia." },
      ],
      honestNote: "Transparencia: Ocean Data no es un socio certificado ni revendedor de Zuper. Asesoramos en evaluación, implementación e integración desde la experiencia en servicio de campo — y le diremos cuándo un socio certificado es la mejor vía.",
      faqTitle: "Las respuestas honestas.",
      faqs: [
        {
          q: "¿Zuper o Zoho FSM — cuál es mejor?",
          a: "Depende de su modelo de despacho, tamaño de equipo y pila contable. Zoho FSM encaja en negocios centrados en Zoho; Zuper puede convenir a equipos que necesitan sus fortalezas específicas de programación. La revisión gratuita mapea su flujo contra ambos.",
        },
        {
          q: "¿Pueden integrar Zuper con QuickBooks?",
          a: "Donde exista un conector soportado, sí — y documentamos exactamente qué se sincroniza y qué no antes de que decida.",
        },
      ],
      cta: "Reservar mi revisión gratuita de 15 minutos",
    },
  },
};

// New commercial pages (2026-10) — merged from the 16-topic brief by search intent.
export const CLUSTER_COPY_EXTRA = {
  "zoho-fsm-vs-zuper": {
    en: {
      path: "/zoho-fsm-vs-zuper",
      docTitle: "Zoho FSM vs Zuper: Honest Field Service Comparison | Ocean Data",
      schemaName: "Zoho FSM versus Zuper comparison for field service businesses",
      eyebrow: "Software guide · Zoho FSM vs Zuper",
      h1: "Zoho FSM vs Zuper: an honest, workflow-first comparison.",
      sub: "Both run scheduling, dispatch and work orders for HVAC, plumbing and field service teams. The right choice depends on your dispatch model, your accounting stack and how much of your business already lives in Zoho. Here's the straight version.",
      painsTitle: "What usually goes wrong when choosing",
      pains: [
        "Feature checklists win over workflow fit",
        "The accounting integration is discovered as an afterthought",
        "Nobody prices the disruption of switching",
        "The losing platform's data has no exit plan",
      ],
      approachTitle: "How they actually compare",
      approach: [
        { tag: "Zoho FSM", text: "Deep Zoho ecosystem: native Zoho Books invoicing, CRM customer records, Zoho Analytics reporting. Strong for SMB teams standardizing on Zoho." },
        { tag: "Zuper", text: "Purpose-built field service with strong scheduling and route optimization and a polished technician app. Suits teams whose center of gravity is the field, not the CRM." },
        { tag: "Accounting", text: "FSM connects natively to Zoho Books and to QuickBooks through the supported connector. Zuper integrates with QuickBooks and Zoho Books through its own connectors — verify the exact sync fields before deciding." },
        { tag: "Decision", text: "We map your lead-to-invoice flow against both and tell you which one loses fewer hours — including when the honest answer is neither." },
      ],
      honestNote: "Transparency: Ocean Data implements Zoho FSM daily and is not a certified Zuper partner or reseller. We disclose that bias openly — this comparison is workflow-first, and where Zuper fits better, we say so and can coordinate with a certified partner.",
      faqTitle: "The honest answers.",
      faqs: [
        {
          q: "Which is cheaper to run?",
          a: "Both are per-seat SaaS and land in a similar range for SMB field teams. The real cost difference is fit: the wrong platform taxes you weekly in workarounds. The free review prices that difference against your actual workflow.",
        },
        {
          q: "Can we migrate from one to the other later?",
          a: "Yes, but treat it as a project: customer and asset history moves, open work orders need cutover planning, and integrations get rebuilt. Choosing right the first time is cheaper — that's what the evaluation is for.",
        },
      ],
      cta: "Book my free 15-minute review",
    },
    fr: {
      path: "/fr/zoho-fsm-vs-zuper",
      docTitle: "Zoho FSM ou Zuper : comparaison honnête pour services sur le terrain | Ocean Data",
      schemaName: "Comparaison Zoho FSM versus Zuper pour entreprises de services",
      eyebrow: "Guide logiciel · Zoho FSM ou Zuper",
      h1: "Zoho FSM ou Zuper : une comparaison honnête, axée sur le flux de travail.",
      sub: "Les deux gèrent planification, répartition et bons de travail pour les équipes de CVAC, plomberie et services sur le terrain. Le bon choix dépend de votre modèle de répartition, de votre pile comptable et de la place que Zoho occupe déjà dans votre entreprise. Voici la version franche.",
      painsTitle: "Ce qui tourne mal quand on choisit",
      pains: [
        "Les listes de fonctions gagnent contre l'adéquation au flux",
        "L'intégration comptable se découvre après coup",
        "Personne ne chiffre la perturbation du changement",
        "Les données de la plateforme perdante n'ont pas de plan de sortie",
      ],
      approachTitle: "La vraie comparaison",
      approach: [
        { tag: "Zoho FSM", text: "Écosystème Zoho profond : facturation Zoho Books native, dossiers clients CRM, rapports Zoho Analytics. Fort pour les PME qui se standardisent sur Zoho." },
        { tag: "Zuper", text: "Service sur le terrain spécialisé, avec planification et optimisation de routes solides et une application technicien soignée. Convient aux équipes dont le centre de gravité est le terrain, pas le CRM." },
        { tag: "Comptabilité", text: "FSM se connecte à Zoho Books en natif et à QuickBooks par le connecteur supporté. Zuper s'intègre à QuickBooks et Zoho Books par ses propres connecteurs — vérifiez les champs synchronisés exacts avant de décider." },
        { tag: "Décision", text: "On cartographie votre flux prospect→facture face aux deux et on vous dit lequel perd le moins d'heures — y compris quand la réponse honnête est « aucun des deux »." },
      ],
      honestNote: "Transparence : Ocean Data implante Zoho FSM quotidiennement et n'est ni partenaire certifié ni revendeur Zuper. On déclare ce biais ouvertement — la comparaison est axée sur le flux, et là où Zuper convient mieux, on le dit et on peut coordonner avec un partenaire certifié.",
      faqTitle: "Les réponses honnêtes.",
      faqs: [
        {
          q: "Lequel coûte le moins cher à rouler ?",
          a: "Les deux sont des SaaS par utilisateur dans une gamme semblable pour les PME de terrain. La vraie différence de coût, c'est l'adéquation : la mauvaise plateforme vous taxe chaque semaine en détours. La revue gratuite chiffre cette différence sur votre flux réel.",
        },
        {
          q: "Peut-on migrer de l'un à l'autre plus tard ?",
          a: "Oui, mais traitez-le comme un projet : l'historique clients et actifs se déplace, les bons de travail ouverts demandent un plan de bascule, et les intégrations se reconstruisent. Bien choisir du premier coup coûte moins cher — c'est à ça que sert l'évaluation.",
        },
      ],
      cta: "Réserver ma revue gratuite de 15 minutes",
    },
    es: {
      path: "/es/zoho-fsm-vs-zuper",
      docTitle: "Zoho FSM vs Zuper: comparación honesta para servicio de campo | Ocean Data",
      schemaName: "Comparación Zoho FSM versus Zuper para empresas de servicio de campo",
      eyebrow: "Guía de software · Zoho FSM vs Zuper",
      h1: "Zoho FSM vs Zuper: una comparación honesta, centrada en el flujo de trabajo.",
      sub: "Ambos manejan programación, despacho y órdenes de trabajo para equipos de HVAC, plomería y servicio de campo. La elección correcta depende de su modelo de despacho, su pila contable y cuánto de su negocio ya vive en Zoho. Aquí está la versión franca.",
      painsTitle: "Lo que suele salir mal al elegir",
      pains: [
        "Las listas de funciones ganan sobre el encaje con el flujo",
        "La integración contable se descubre como ocurrencia tardía",
        "Nadie le pone precio a la disrupción del cambio",
        "Los datos de la plataforma perdedora no tienen plan de salida",
      ],
      approachTitle: "Cómo se comparan realmente",
      approach: [
        { tag: "Zoho FSM", text: "Ecosistema Zoho profundo: facturación nativa con Zoho Books, expedientes de cliente en CRM, reportes con Zoho Analytics. Fuerte para pymes que se estandarizan en Zoho." },
        { tag: "Zuper", text: "Servicio de campo especializado con programación y optimización de rutas sólidas y una app de técnico pulida. Conviene a equipos cuyo centro de gravedad es el campo, no el CRM." },
        { tag: "Contabilidad", text: "FSM conecta nativamente con Zoho Books y con QuickBooks mediante el conector soportado. Zuper se integra con QuickBooks y Zoho Books por sus propios conectores — verifique los campos exactos de sincronización antes de decidir." },
        { tag: "Decisión", text: "Mapeamos su flujo prospecto→factura contra ambos y le decimos cuál pierde menos horas — incluso cuando la respuesta honesta es ninguno." },
      ],
      honestNote: "Transparencia: Ocean Data implementa Zoho FSM a diario y no es socio certificado ni revendedor de Zuper. Declaramos ese sesgo abiertamente — la comparación se centra en el flujo, y donde Zuper encaja mejor, lo decimos y podemos coordinar con un socio certificado.",
      faqTitle: "Las respuestas honestas.",
      faqs: [
        {
          q: "¿Cuál es más barato de operar?",
          a: "Ambos son SaaS por usuario en un rango similar para equipos de campo pyme. La verdadera diferencia de costo es el encaje: la plataforma equivocada le cobra cada semana en rodeos. La revisión gratuita le pone cifras a esa diferencia sobre su flujo real.",
        },
        {
          q: "¿Podemos migrar de uno a otro después?",
          a: "Sí, pero trátelo como un proyecto: el historial de clientes y activos se mueve, las órdenes abiertas necesitan un plan de corte y las integraciones se reconstruyen. Elegir bien la primera vez es más barato — para eso es la evaluación.",
        },
      ],
      cta: "Reservar mi revisión gratuita de 15 minutos",
    },
  },

  "zoho-projects-construction": {
    en: {
      path: "/zoho-projects-construction",
      docTitle: "Zoho Projects for Construction: Estimate-to-Invoice Workflows | Ocean Data",
      schemaName: "Zoho Projects implementation for construction workflows",
      eyebrow: "Software guide · Zoho Projects",
      h1: "Zoho Projects for construction: from estimate to final invoice, one record.",
      sub: "Project coordination, task ownership, milestones, documentation and financial handoffs for contractors — with Zoho CRM upstream, Zoho Books or QuickBooks downstream, and Autodesk Construction Cloud connected where it matters.",
      painsTitle: "Where construction hours leak",
      pains: [
        "The estimate and the project live in different worlds",
        "Change orders get priced twice and invoiced late",
        "Site photos and RFIs sit in text threads",
        "Progress billing waits for month-end memory",
      ],
      approachTitle: "How an Ocean Data construction setup works",
      approach: [
        { tag: "Native", text: "Zoho Projects tasks, milestones, documents and time logs — configured around how your PMs and site leads actually run a job." },
        { tag: "Configuration", text: "Templates per job type, approval paths for change orders, and role-based views: office, site and client see their own slice of the same record." },
        { tag: "Integration", text: "Zoho CRM carries the estimate into project setup; Zoho Books or QuickBooks handles progress invoicing; Autodesk Construction Cloud files link to the tasks they belong to." },
        { tag: "Custom API", text: "Where a handoff isn't natively covered — a specific ACC workflow, a legacy estimating tool — we build it explicitly and document the limits." },
      ],
      honestNote: "Honest note: Zoho Projects coordinates the work; it does not replace Autodesk Construction Cloud for models, drawings or the CDE. We connect the two so each system does what it's best at — and we tell you when a heavier platform is genuinely the right call.",
      faqTitle: "The honest answers.",
      faqs: [
        {
          q: "Does Zoho Projects replace Procore or Autodesk?",
          a: "No — and it shouldn't. ACC or Procore remain the system of record for models and drawings. Zoho Projects coordinates tasks, milestones and financial handoffs around them, connected so nothing gets re-typed.",
        },
        {
          q: "Can progress billing tie to project milestones?",
          a: "Yes — milestones in Zoho Projects can trigger progress invoicing in Zoho Books, so billing follows the work instead of the calendar.",
        },
      ],
      cta: "Book my free 15-minute review",
    },
    fr: {
      path: "/fr/zoho-projects-construction",
      docTitle: "Zoho Projects pour la construction : flux soumission→facture | Ocean Data",
      schemaName: "Implantation Zoho Projects pour les flux de construction",
      eyebrow: "Guide logiciel · Zoho Projects",
      h1: "Zoho Projects pour la construction : de la soumission à la dernière facture, un seul dossier.",
      sub: "Coordination de projet, responsabilités, jalons, documentation et transferts financiers pour les entrepreneurs — avec Zoho CRM en amont, Zoho Books ou QuickBooks en aval, et Autodesk Construction Cloud connecté là où ça compte.",
      painsTitle: "Où les heures de construction fuient",
      pains: [
        "La soumission et le projet vivent dans des mondes différents",
        "Les ordres de changement sont prisés deux fois et facturés en retard",
        "Les photos de chantier et les RFI dorment dans des fils de textos",
        "La facturation d'avancement attend la mémoire de fin de mois",
      ],
      approachTitle: "Comment fonctionne une configuration construction par Ocean Data",
      approach: [
        { tag: "Natif", text: "Tâches, jalons, documents et feuilles de temps Zoho Projects — configurés selon la façon dont vos chargés de projet et chefs de chantier roulent vraiment un contrat." },
        { tag: "Configuration", text: "Gabarits par type de contrat, chemins d'approbation pour les changements, et vues par rôle : bureau, chantier et client voient chacun leur part du même dossier." },
        { tag: "Intégration", text: "Zoho CRM transporte la soumission jusqu'au démarrage du projet; Zoho Books ou QuickBooks gère la facturation d'avancement; les fichiers Autodesk Construction Cloud se lient aux tâches concernées." },
        { tag: "API sur mesure", text: "Quand un transfert n'est pas couvert nativement — un flux ACC précis, un outil d'estimation existant — on le construit explicitement et on en documente les limites." },
      ],
      honestNote: "Note honnête : Zoho Projects coordonne le travail; il ne remplace pas Autodesk Construction Cloud pour les maquettes, les plans ou le CDE. On connecte les deux pour que chaque système fasse ce qu'il fait de mieux — et on vous dit quand une plateforme plus lourde est vraiment le bon choix.",
      faqTitle: "Les réponses honnêtes.",
      faqs: [
        {
          q: "Est-ce que Zoho Projects remplace Procore ou Autodesk ?",
          a: "Non — et il ne devrait pas. ACC ou Procore restent la référence pour les maquettes et les plans. Zoho Projects coordonne tâches, jalons et transferts financiers autour d'eux, connecté pour que rien ne soit ressaisi.",
        },
        {
          q: "La facturation d'avancement peut-elle suivre les jalons ?",
          a: "Oui — les jalons de Zoho Projects peuvent déclencher la facturation progressive dans Zoho Books, pour que la facturation suive le travail plutôt que le calendrier.",
        },
      ],
      cta: "Réserver ma revue gratuite de 15 minutes",
    },
    es: {
      path: "/es/zoho-projects-construction",
      docTitle: "Zoho Projects para construcción: flujos de cotización a factura | Ocean Data",
      schemaName: "Implementación de Zoho Projects para flujos de construcción",
      eyebrow: "Guía de software · Zoho Projects",
      h1: "Zoho Projects para construcción: de la cotización a la última factura, un solo expediente.",
      sub: "Coordinación de proyectos, responsables, hitos, documentación y traspasos financieros para contratistas — con Zoho CRM aguas arriba, Zoho Books o QuickBooks aguas abajo, y Autodesk Construction Cloud conectado donde importa.",
      painsTitle: "Dónde se escapan las horas de construcción",
      pains: [
        "La cotización y el proyecto viven en mundos distintos",
        "Las órdenes de cambio se cotizan dos veces y se facturan tarde",
        "Las fotos de obra y los RFI duermen en hilos de texto",
        "La facturación por avance espera la memoria de fin de mes",
      ],
      approachTitle: "Cómo funciona una configuración de construcción con Ocean Data",
      approach: [
        { tag: "Nativo", text: "Tareas, hitos, documentos y registros de tiempo de Zoho Projects — configurados según cómo sus gerentes de proyecto y jefes de obra realmente ejecutan un trabajo." },
        { tag: "Configuración", text: "Plantillas por tipo de trabajo, rutas de aprobación para órdenes de cambio y vistas por rol: oficina, obra y cliente ven cada uno su parte del mismo expediente." },
        { tag: "Integración", text: "Zoho CRM lleva la cotización hasta el arranque del proyecto; Zoho Books o QuickBooks maneja la facturación por avance; los archivos de Autodesk Construction Cloud se vinculan a sus tareas." },
        { tag: "API a medida", text: "Cuando un traspaso no está cubierto nativamente — un flujo de ACC específico, una herramienta de estimación heredada — lo construimos explícitamente y documentamos los límites." },
      ],
      honestNote: "Nota honesta: Zoho Projects coordina el trabajo; no reemplaza a Autodesk Construction Cloud para modelos, planos o el CDE. Conectamos ambos para que cada sistema haga lo que mejor hace — y le decimos cuándo una plataforma más pesada es realmente la decisión correcta.",
      faqTitle: "Las respuestas honestas.",
      faqs: [
        {
          q: "¿Zoho Projects reemplaza a Procore o Autodesk?",
          a: "No — y no debería. ACC o Procore siguen siendo la referencia para modelos y planos. Zoho Projects coordina tareas, hitos y traspasos financieros a su alrededor, conectado para que nada se reescriba.",
        },
        {
          q: "¿La facturación por avance puede seguir los hitos?",
          a: "Sí — los hitos de Zoho Projects pueden disparar la facturación por progreso en Zoho Books, para que la facturación siga al trabajo y no al calendario.",
        },
      ],
      cta: "Reservar mi revisión gratuita de 15 minutos",
    },
  },

  "zoho-fsm-accounting": {
    en: {
      path: "/zoho-fsm-accounting",
      docTitle: "Zoho FSM Accounting Integrations: Zoho Books & QuickBooks | Ocean Data",
      schemaName: "Zoho FSM accounting integrations with Zoho Books and QuickBooks",
      eyebrow: "Software guide · Zoho FSM + accounting",
      h1: "Zoho FSM + your accounting: invoices that write themselves.",
      sub: "Field service only pays off when the money moves. Zoho FSM connects natively to Zoho Books — and to QuickBooks through the supported integration — so completed work orders become invoices without re-entry, and the field and the books finally tell the same story.",
      painsTitle: "Where the money leaks between field and office",
      pains: [
        "The tech completes the work; the office rebuilds the invoice from a photo",
        "Parts used in the field never make it onto the bill",
        "Tax and price rules differ between the field tool and the books",
        "Month-end reconciliation reads like a detective novel",
      ],
      approachTitle: "How we connect FSM to your books",
      approach: [
        { tag: "Native", text: "Zoho FSM → Zoho Books: customers, invoices and payments flow natively. A completed work order becomes a draft invoice with time and parts attached." },
        { tag: "Configuration", text: "Item lists, tax rules and price books aligned between field and books so an invoice means the same thing in both systems." },
        { tag: "Integration", text: "Keeping QuickBooks? The supported FSM–QuickBooks connector carries invoices and customers across — we document exactly what syncs and what doesn't before you commit." },
        { tag: "Custom API", text: "Complex multi-entity books, inventory valuation or a legacy ERP? We assess feasibility honestly and build the bridge only where it's maintainable." },
      ],
      honestNote: "Honest note: 'integrated' is not a binary. Customers, invoices, payments and items sync well; edge cases — multi-entity consolidations, advanced inventory costing — need verification against your exact setup. We test with your data in the audit, not after you've paid.",
      faqTitle: "The honest answers.",
      faqs: [
        {
          q: "Can we keep QuickBooks and still use Zoho FSM?",
          a: "Yes — the supported integration carries invoices and customers from FSM to QuickBooks, so field operations modernize while your accountant keeps the books they know.",
        },
        {
          q: "What exactly syncs between FSM and Zoho Books?",
          a: "Customers, invoices, payments and item details flow natively. We confirm the exact field mapping against your chart of accounts during the free process mapping audit.",
        },
      ],
      cta: "Book my free 15-minute review",
    },
    fr: {
      path: "/fr/zoho-fsm-accounting",
      docTitle: "Intégrations comptables Zoho FSM : Zoho Books et QuickBooks | Ocean Data",
      schemaName: "Intégrations comptables Zoho FSM avec Zoho Books et QuickBooks",
      eyebrow: "Guide logiciel · Zoho FSM + comptabilité",
      h1: "Zoho FSM + votre comptabilité : des factures qui s'écrivent toutes seules.",
      sub: "Le service sur le terrain ne rapporte que si l'argent circule. Zoho FSM se connecte à Zoho Books en natif — et à QuickBooks par l'intégration supportée — pour que les bons de travail complétés deviennent des factures sans ressaisie.",
      painsTitle: "Où l'argent fuit entre le terrain et le bureau",
      pains: [
        "Le technicien finit le travail; le bureau reconstruit la facture d'après une photo",
        "Les pièces utilisées sur le terrain n'arrivent jamais sur la facture",
        "Les règles de taxes et de prix diffèrent entre l'outil de terrain et les livres",
        "La conciliation de fin de mois se lit comme un roman policier",
      ],
      approachTitle: "Comment on connecte FSM à vos livres",
      approach: [
        { tag: "Natif", text: "Zoho FSM → Zoho Books : clients, factures et paiements circulent nativement. Un bon de travail complété devient une facture provisoire avec temps et pièces joints." },
        { tag: "Configuration", text: "Listes d'items, règles de taxes et listes de prix alignées entre terrain et comptabilité pour qu'une facture veuille dire la même chose dans les deux systèmes." },
        { tag: "Intégration", text: "Vous gardez QuickBooks ? Le connecteur supporté FSM–QuickBooks transporte factures et clients — on documente exactement ce qui se synchronise avant que vous vous engagiez." },
        { tag: "API sur mesure", text: "Livres multi-entités complexes, valorisation d'inventaire ou ERP existant ? On évalue la faisabilité honnêtement et on ne construit le pont que là où il reste maintenable." },
      ],
      honestNote: "Note honnête : « intégré » n'est pas binaire. Clients, factures, paiements et items se synchronisent bien; les cas limites — consolidations multi-entités, coût d'inventaire avancé — demandent une vérification sur votre configuration exacte. On teste avec vos données pendant l'audit, pas après que vous ayez payé.",
      faqTitle: "Les réponses honnêtes.",
      faqs: [
        {
          q: "Peut-on garder QuickBooks et utiliser Zoho FSM quand même ?",
          a: "Oui — l'intégration supportée transporte factures et clients de FSM vers QuickBooks, pour moderniser les opérations de terrain pendant que votre comptable garde les livres qu'il connaît.",
        },
        {
          q: "Qu'est-ce qui se synchronise exactement entre FSM et Zoho Books ?",
          a: "Clients, factures, paiements et détails d'items circulent nativement. On confirme la correspondance exacte des champs avec votre plan comptable pendant l'audit de cartographie gratuit.",
        },
      ],
      cta: "Réserver ma revue gratuite de 15 minutes",
    },
    es: {
      path: "/es/zoho-fsm-accounting",
      docTitle: "Integraciones contables de Zoho FSM: Zoho Books y QuickBooks | Ocean Data",
      schemaName: "Integraciones contables de Zoho FSM con Zoho Books y QuickBooks",
      eyebrow: "Guía de software · Zoho FSM + contabilidad",
      h1: "Zoho FSM + su contabilidad: facturas que se escriben solas.",
      sub: "El servicio de campo solo rinde cuando el dinero se mueve. Zoho FSM se conecta nativamente a Zoho Books — y a QuickBooks mediante la integración soportada — para que las órdenes de trabajo completadas se vuelvan facturas sin reescritura.",
      painsTitle: "Dónde se fuga el dinero entre campo y oficina",
      pains: [
        "El técnico termina el trabajo; la oficina reconstruye la factura desde una foto",
        "Las piezas usadas en campo nunca llegan a la factura",
        "Las reglas de impuestos y precios difieren entre la herramienta de campo y los libros",
        "La conciliación de fin de mes se lee como novela de misterio",
      ],
      approachTitle: "Cómo conectamos FSM a sus libros",
      approach: [
        { tag: "Nativo", text: "Zoho FSM → Zoho Books: clientes, facturas y pagos fluyen nativamente. Una orden completada se vuelve factura borrador con tiempo y piezas adjuntos." },
        { tag: "Configuración", text: "Listas de artículos, reglas de impuestos y listas de precios alineadas entre campo y contabilidad para que una factura signifique lo mismo en ambos sistemas." },
        { tag: "Integración", text: "¿Conserva QuickBooks? El conector soportado FSM–QuickBooks lleva facturas y clientes — documentamos exactamente qué se sincroniza antes de que se comprometa." },
        { tag: "API a medida", text: "¿Libros multientidad complejos, valuación de inventario o un ERP heredado? Evaluamos la viabilidad con honestidad y solo construimos el puente donde sea mantenible." },
      ],
      honestNote: "Nota honesta: «integrado» no es binario. Clientes, facturas, pagos y artículos se sincronizan bien; los casos límite — consolidaciones multientidad, costeo de inventario avanzado — requieren verificación contra su configuración exacta. Probamos con sus datos en la auditoría, no después de que pague.",
      faqTitle: "Las respuestas honestas.",
      faqs: [
        {
          q: "¿Podemos conservar QuickBooks y aun así usar Zoho FSM?",
          a: "Sí — la integración soportada lleva facturas y clientes de FSM a QuickBooks, modernizando las operaciones de campo mientras su contador conserva los libros que conoce.",
        },
        {
          q: "¿Qué se sincroniza exactamente entre FSM y Zoho Books?",
          a: "Clientes, facturas, pagos y detalles de artículos fluyen nativamente. Confirmamos la correspondencia exacta de campos con su plan de cuentas durante la auditoría gratuita de mapeo de procesos.",
        },
      ],
      cta: "Reservar mi revisión gratuita de 15 minutos",
    },
  },
};

// Workflow stage maps (which tool carries which stage) + regional/capability notes.
export const CLUSTER_EXTRAS = {
  "zoho-fsm": {
    en: {
      workflowTitle: "The field service journey — and who carries each stage",
      workflow: [
        { stage: "Lead", tool: "Zoho CRM" },
        { stage: "Estimate", tool: "Zoho CRM / Books" },
        { stage: "Schedule", tool: "Zoho FSM" },
        { stage: "Dispatch", tool: "Zoho FSM" },
        { stage: "Work order", tool: "Zoho FSM mobile" },
        { stage: "Technician completion", tool: "Zoho FSM mobile" },
        { stage: "Customer approval", tool: "Zoho FSM" },
        { stage: "Invoice", tool: "Zoho Books or QuickBooks" },
        { stage: "Payment", tool: "Zoho Books or QuickBooks" },
        { stage: "Reporting", tool: "Zoho Analytics" },
      ],
      regionsNote: "Implemented remotely and on-site from Montréal (Canada) and Phoenix (US) — serving Québec, Arizona, Florida and the rest of Canada and the US, in English, French and Spanish.",
    },
    fr: {
      workflowTitle: "Le parcours de service — et qui porte chaque étape",
      workflow: [
        { stage: "Prospect", tool: "Zoho CRM" },
        { stage: "Soumission", tool: "Zoho CRM / Books" },
        { stage: "Planification", tool: "Zoho FSM" },
        { stage: "Répartition", tool: "Zoho FSM" },
        { stage: "Bon de travail", tool: "Zoho FSM mobile" },
        { stage: "Complétion technicien", tool: "Zoho FSM mobile" },
        { stage: "Approbation client", tool: "Zoho FSM" },
        { stage: "Facture", tool: "Zoho Books ou QuickBooks" },
        { stage: "Paiement", tool: "Zoho Books ou QuickBooks" },
        { stage: "Rapports", tool: "Zoho Analytics" },
      ],
      regionsNote: "Implanté à distance et sur place depuis Montréal (Canada) et Phoenix (États-Unis) — au service du Québec, de l'Arizona, de la Floride et du reste du Canada et des États-Unis, en français, en anglais et en espagnol.",
    },
    es: {
      workflowTitle: "El recorrido del servicio — y quién lleva cada etapa",
      workflow: [
        { stage: "Prospecto", tool: "Zoho CRM" },
        { stage: "Cotización", tool: "Zoho CRM / Books" },
        { stage: "Programación", tool: "Zoho FSM" },
        { stage: "Despacho", tool: "Zoho FSM" },
        { stage: "Orden de trabajo", tool: "Zoho FSM móvil" },
        { stage: "Cierre del técnico", tool: "Zoho FSM móvil" },
        { stage: "Aprobación del cliente", tool: "Zoho FSM" },
        { stage: "Factura", tool: "Zoho Books o QuickBooks" },
        { stage: "Pago", tool: "Zoho Books o QuickBooks" },
        { stage: "Reportes", tool: "Zoho Analytics" },
      ],
      regionsNote: "Implementado de forma remota y presencial desde Montreal (Canadá) y Phoenix (EE. UU.) — sirviendo a Québec, Arizona, Florida y el resto de Canadá y Estados Unidos, en inglés, francés y español.",
    },
  },
  "zoho-iot-connected-buildings": {
    en: {
      workflowTitle: "The connected-building chain — from asset to report",
      workflow: [
        { stage: "Building asset", tool: "Your equipment" },
        { stage: "Sensor or gateway", tool: "Verified hardware" },
        { stage: "Supported telemetry", tool: "Zoho IoT" },
        { stage: "Monitoring dashboard", tool: "Zoho IoT" },
        { stage: "Threshold detection", tool: "Zoho IoT rules" },
        { stage: "Maintenance action", tool: "Zoho FSM work order" },
        { stage: "Service record", tool: "Zoho CRM / FSM" },
        { stage: "Cost & energy reporting", tool: "Zoho Analytics" },
      ],
      extraNote: "Solar and energy telemetry: feasibility depends on your actual inverter, meter, gateway and communication protocol (e.g. Modbus/SunSpec or vendor APIs). We verify telemetry availability against your equipment list before proposing dashboards. We do not promise native solar forecasting, inverter fault diagnosis or automated energy optimization — where those are needed, we'll tell you what Zoho IoT supports and what requires a specialized platform.",
    },
    fr: {
      workflowTitle: "La chaîne du bâtiment connecté — de l'actif au rapport",
      workflow: [
        { stage: "Actif du bâtiment", tool: "Votre équipement" },
        { stage: "Capteur ou passerelle", tool: "Matériel validé" },
        { stage: "Télémétrie supportée", tool: "Zoho IoT" },
        { stage: "Tableau de surveillance", tool: "Zoho IoT" },
        { stage: "Détection de seuils", tool: "Règles Zoho IoT" },
        { stage: "Action de maintenance", tool: "Bon de travail Zoho FSM" },
        { stage: "Dossier de service", tool: "Zoho CRM / FSM" },
        { stage: "Rapports coûts et énergie", tool: "Zoho Analytics" },
      ],
      extraNote: "Télémétrie solaire et énergétique : la faisabilité dépend de votre onduleur, compteur, passerelle et protocole de communication réels (p. ex. Modbus/SunSpec ou API du fournisseur). On vérifie la disponibilité de la télémétrie sur votre liste d'équipement avant de proposer des tableaux de bord. On ne promet ni prévision solaire native, ni diagnostic de défaillance d'onduleur, ni optimisation énergétique automatisée — là où c'est nécessaire, on vous dit ce que Zoho IoT supporte et ce qui exige une plateforme spécialisée.",
    },
    es: {
      workflowTitle: "La cadena del edificio conectado — del activo al reporte",
      workflow: [
        { stage: "Activo del edificio", tool: "Su equipo" },
        { stage: "Sensor o pasarela", tool: "Hardware verificado" },
        { stage: "Telemetría soportada", tool: "Zoho IoT" },
        { stage: "Tablero de monitoreo", tool: "Zoho IoT" },
        { stage: "Detección de umbrales", tool: "Reglas de Zoho IoT" },
        { stage: "Acción de mantenimiento", tool: "Orden de trabajo Zoho FSM" },
        { stage: "Expediente de servicio", tool: "Zoho CRM / FSM" },
        { stage: "Reportes de costos y energía", tool: "Zoho Analytics" },
      ],
      extraNote: "Telemetría solar y energética: la viabilidad depende de su inversor, medidor, pasarela y protocolo de comunicación reales (p. ej. Modbus/SunSpec o API del proveedor). Verificamos la disponibilidad de telemetría contra su lista de equipos antes de proponer tableros. No prometemos pronóstico solar nativo, diagnóstico de fallas de inversor ni optimización energética automatizada — donde se necesiten, le diremos qué soporta Zoho IoT y qué requiere una plataforma especializada.",
    },
  },
  "zoho-fsm-vs-zuper": {
    en: {
      workflowTitle: "The journey we map against both platforms",
      workflow: [
        { stage: "Lead", tool: "CRM" },
        { stage: "Estimate", tool: "CRM / field tool" },
        { stage: "Schedule & dispatch", tool: "FSM or Zuper" },
        { stage: "Work order & completion", tool: "FSM or Zuper" },
        { stage: "Invoice & payment", tool: "Zoho Books or QuickBooks" },
        { stage: "Reporting", tool: "Analytics" },
      ],
    },
    fr: {
      workflowTitle: "Le parcours qu'on cartographie face aux deux plateformes",
      workflow: [
        { stage: "Prospect", tool: "CRM" },
        { stage: "Soumission", tool: "CRM / outil de terrain" },
        { stage: "Planification et répartition", tool: "FSM ou Zuper" },
        { stage: "Bon de travail et complétion", tool: "FSM ou Zuper" },
        { stage: "Facture et paiement", tool: "Zoho Books ou QuickBooks" },
        { stage: "Rapports", tool: "Analytique" },
      ],
    },
    es: {
      workflowTitle: "El recorrido que mapeamos contra ambas plataformas",
      workflow: [
        { stage: "Prospecto", tool: "CRM" },
        { stage: "Cotización", tool: "CRM / herramienta de campo" },
        { stage: "Programación y despacho", tool: "FSM o Zuper" },
        { stage: "Orden de trabajo y cierre", tool: "FSM o Zuper" },
        { stage: "Factura y pago", tool: "Zoho Books o QuickBooks" },
        { stage: "Reportes", tool: "Analítica" },
      ],
    },
  },
  "zoho-projects-construction": {
    en: {
      workflowTitle: "The construction journey — and who carries each stage",
      workflow: [
        { stage: "Estimate", tool: "Zoho CRM" },
        { stage: "Project setup", tool: "Zoho Projects" },
        { stage: "Tasks & documentation", tool: "Zoho Projects + Autodesk files" },
        { stage: "Site execution", tool: "Zoho Projects mobile" },
        { stage: "Change orders", tool: "Zoho Projects + approval" },
        { stage: "Progress tracking", tool: "Zoho Projects milestones" },
        { stage: "Billing", tool: "Zoho Books or QuickBooks" },
        { stage: "Financial reporting", tool: "Zoho Analytics / Books" },
      ],
    },
    fr: {
      workflowTitle: "Le parcours de construction — et qui porte chaque étape",
      workflow: [
        { stage: "Soumission", tool: "Zoho CRM" },
        { stage: "Démarrage du projet", tool: "Zoho Projects" },
        { stage: "Tâches et documentation", tool: "Zoho Projects + fichiers Autodesk" },
        { stage: "Exécution chantier", tool: "Zoho Projects mobile" },
        { stage: "Ordres de changement", tool: "Zoho Projects + approbation" },
        { stage: "Suivi d'avancement", tool: "Jalons Zoho Projects" },
        { stage: "Facturation", tool: "Zoho Books ou QuickBooks" },
        { stage: "Rapports financiers", tool: "Zoho Analytics / Books" },
      ],
    },
    es: {
      workflowTitle: "El recorrido de construcción — y quién lleva cada etapa",
      workflow: [
        { stage: "Cotización", tool: "Zoho CRM" },
        { stage: "Arranque del proyecto", tool: "Zoho Projects" },
        { stage: "Tareas y documentación", tool: "Zoho Projects + archivos Autodesk" },
        { stage: "Ejecución en obra", tool: "Zoho Projects móvil" },
        { stage: "Órdenes de cambio", tool: "Zoho Projects + aprobación" },
        { stage: "Seguimiento de avance", tool: "Hitos de Zoho Projects" },
        { stage: "Facturación", tool: "Zoho Books o QuickBooks" },
        { stage: "Reportes financieros", tool: "Zoho Analytics / Books" },
      ],
    },
  },
  "zoho-fsm-accounting": {
    en: {
      workflowTitle: "From completed work order to reconciled payment",
      workflow: [
        { stage: "Work order completed", tool: "Zoho FSM" },
        { stage: "Time & parts captured", tool: "Zoho FSM mobile" },
        { stage: "Invoice drafted", tool: "FSM → Books (native) / → QuickBooks (connector)" },
        { stage: "Approval", tool: "Your rule" },
        { stage: "Sent & paid", tool: "Zoho Books or QuickBooks" },
        { stage: "Reconciliation & reporting", tool: "Books / QuickBooks + Analytics" },
      ],
    },
    fr: {
      workflowTitle: "Du bon de travail complété au paiement concilié",
      workflow: [
        { stage: "Bon de travail complété", tool: "Zoho FSM" },
        { stage: "Temps et pièces saisis", tool: "Zoho FSM mobile" },
        { stage: "Facture préparée", tool: "FSM → Books (natif) / → QuickBooks (connecteur)" },
        { stage: "Approbation", tool: "Votre règle" },
        { stage: "Envoyée et payée", tool: "Zoho Books ou QuickBooks" },
        { stage: "Conciliation et rapports", tool: "Books / QuickBooks + Analytics" },
      ],
    },
    es: {
      workflowTitle: "De la orden completada al pago conciliado",
      workflow: [
        { stage: "Orden de trabajo completada", tool: "Zoho FSM" },
        { stage: "Tiempo y piezas capturados", tool: "Zoho FSM móvil" },
        { stage: "Factura preparada", tool: "FSM → Books (nativo) / → QuickBooks (conector)" },
        { stage: "Aprobación", tool: "Su regla" },
        { stage: "Enviada y cobrada", tool: "Zoho Books o QuickBooks" },
        { stage: "Conciliación y reportes", tool: "Books / QuickBooks + Analytics" },
      ],
    },
  },
};
